/* eslint-disable no-alert */

import { Controller } from '@hotwired/stimulus';

// Fetch resources using the Fetch API by configuring DOM elements and invoking
// the fetch on element actions. Turbo streams in the response body will be
// rendered on OK responses. Useful for e.g. fetching Turbo stream updates on
// element clicks.
// Note: this handles sending CSRF tokens for non-GET requests.
// Note: this dispatches similar lifecycle events to the Turbo fetch events.
//       See: https://turbo.hotwired.dev/reference/events.
// Ex:
//  <button
//    data-controller="turbo-fetch"
//    data-action="click->turbo-fetch#fetch"
//    data-turbo-fetch-resource-url-value="some-URL"
//    data-turbo-fetch-method-value="DELETE"
//  >
//  </button>
// Ex:
//  <div
//    data-controller="turbo-fetch"
//    data-turbo-fetch-resource-url-value="some-URL"
//    data-turbo-fetch-method-value="GET"
//    data-turbo-fetch-auto-trigger-value="true"
//  >
//  </div>

export default class extends Controller {
  static values = {
    resourceUrl: String,
    method: { type: String, default: 'GET' },
    contentType: { type: String, default: 'application/json' },
    accept: {
      type: String,
      default: 'text/vnd.turbo-stream.html, text/html, application/xhtml+xml',
    },
    redirect: { type: String, default: 'error' },
    confirm: { type: String },
    autoTrigger: { type: Boolean, default: false },
  };

  connect() {
    this._defaultHeaders = new Headers();
    this.element.addEventListener(`${this.identifier}:trigger`, this._triggerFetch);

    if (this.autoTriggerValue) {
      this._triggerFetch();
    }
  }

  disconnect() {
    this.element.removeEventListener(`${this.identifier}:trigger`, this._triggerFetch);
  }

  fetch(event = {}) {
    const { detail = {} } = event;
    const { body } = detail;

    let fetchUrl = this.resourceUrlValue;
    const options = {
      method: this.methodValue,
      headers: this._fetchHeaders,
      redirect: this.redirectValue,
    };

    if (this.confirmValue && !window.confirm(this.confirmValue)) {
      return;
    }

    // Apply the serialized JSON body and query params on the URL if the method
    // is GET or HEAD. These requests don't have a body
    if (this.methodValue.toUpperCase() === 'GET' || this.methodValue.toUpperCase() === 'HEAD') {
      fetchUrl = this._applyGetOrHeadBodytoResourceUrl(body);
    } else {
      options.body = body;
    }

    this.dispatch('before-fetch-request', {
      detail: {
        url: fetchUrl,
        fetchOptions: options,
      },
      bubbles: false,
    });

    fetch(fetchUrl, options).then((response) => {
      this.dispatch('before-fetch-response', {
        detail: {
          url: fetchUrl,
          fetchOptions: options,
          fetchResponse: { response },
        },
        bubbles: false,
      });
      if (response.ok) {
        response.text().then((html) => {
          Turbo.renderStreamMessage(html);
        });

        this.dispatch('fetch-response-ok', {
          detail: {
            url: fetchUrl,
            fetchOptions: options,
            fetchResponse: { response },
          },
          bubbles: false,
        });
      } else {
        this.dispatch('fetch-response-not-ok', {
          detail: {
            url: fetchUrl,
            fetchOptions: options,
            fetchResponse: { response },
          },
          bubbles: false,
        });
      }
    });
  }

  // private

  get _fetchHeaders() {
    const headers = new Headers(this._defaultHeaders);

    headers.append('X-Requested-With', 'XMLHttpRequest');
    headers.append('Content-Type', this.contentTypeValue);
    headers.append('Accept', this.acceptValue);

    if (this.methodValue !== 'GET') {
      headers.append('X-CSRF-TOKEN', document.querySelector('meta[name="csrf-token"]').content);
    }

    return headers;
  }

  _triggerFetch() {
    this.fetch();
  }

  _applyGetOrHeadBodytoResourceUrl(serializedJsonBody) {
    try {
      const bodyObject = JSON.parse(serializedJsonBody);
      const resourceUrlObject = new URL(this.resourceUrlValue, window.location);

      Object.entries(bodyObject).forEach(([key, value]) => {
        resourceUrlObject.searchParams.set(key, value);
      });

      return resourceUrlObject.pathname + resourceUrlObject.search;
    } catch (error) {
      return this.resourceUrlValue;
    }
  }
}
